/**
 * Issue Tracker — admin queue for everything reported via FeedbackButton.
 *
 * Path: /app/admin/tracker  (company_admin + super_admin)
 *
 * Layout:
 *   - Sidebar: filter chips by status + module
 *   - Main: stats hero + table of items
 *   - Detail drawer: title, description, severity dropdown, status pipeline,
 *                    comments thread, "show on roadmap" toggle
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../components/ui/sheet";
import { Bug, Lightbulb, MagicWand, Question, ArrowsClockwise, Megaphone } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_META = {
  bug: { Icon: Bug, color: "text-red-600", label: "Bug" },
  feature: { Icon: Lightbulb, color: "text-amber-600", label: "Feature" },
  improvement: { Icon: MagicWand, color: "text-indigo-600", label: "Improvement" },
  question: { Icon: Question, color: "text-zinc-600", label: "Question" },
};

const SEV_BADGE = {
  blocker: { className: "bg-red-600 hover:bg-red-600 text-white", label: "🚨 Blocker" },
  high: { className: "bg-orange-100 text-orange-900 border-orange-300", variant: "outline", label: "🔴 High" },
  normal: { className: "bg-zinc-100 text-zinc-700 border-zinc-200", variant: "outline", label: "🟡 Normal" },
  low: { className: "bg-zinc-50 text-zinc-500 border-zinc-200", variant: "outline", label: "⚪ Low" },
};

const STATUSES = ["new", "triaged", "in_progress", "resolved", "verified", "wont_do"];

export default function IssueTracker() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ by_module: {}, by_status: {}, by_severity: {}, by_type: {}, open: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", module: "", severity: "", type: "" });
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(null);

  const load = async () => {
    try {
      const [it, st] = await Promise.all([
        api.get("/tracker/items", { params: filter }),
        api.get("/tracker/stats"),
      ]);
      setItems(it.data.items || []);
      setStats(st.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      (i.title || "").toLowerCase().includes(q) ||
      (i.description || "").toLowerCase().includes(q) ||
      (i.reporter_email || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  return (
    <AppShell title="Issue Tracker">
      <div className="max-w-7xl mx-auto" data-testid="tracker-page">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-zinc-900">Platform Issue &amp; Feature Tracker</h1>
            <p className="text-sm text-zinc-600 mt-1">Cross-tenant queue. Every &quot;Report&quot; button on every customer&apos;s SaarOne lands here. Triage, assign, ship.</p>
          </div>
          <Button variant="outline" onClick={load} data-testid="refresh-btn">
            <ArrowsClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatBox label="Open" value={stats.open} highlight />
          <StatBox label="Blockers" value={stats.by_severity?.blocker || 0} highlight={!!stats.by_severity?.blocker} />
          <StatBox label="In progress" value={stats.by_status?.in_progress || 0} />
          <StatBox label="Resolved" value={stats.by_status?.resolved || 0} />
          <StatBox label="Total" value={stats.total || 0} />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Input placeholder="Search title / description…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" data-testid="search-input" />
          <FilterPills label="Status" current={filter.status} options={STATUSES} onPick={(v) => setFilter({ ...filter, status: v })} testid="filter-status" />
          <FilterPills label="Type" current={filter.type} options={["bug", "feature", "improvement", "question"]} onPick={(v) => setFilter({ ...filter, type: v })} testid="filter-type" />
        </div>

        {/* Two-col: module sidebar + table */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <aside className="lg:col-span-1">
            <Card className="p-3">
              <div className="text-xs font-semibold text-zinc-500 uppercase mb-2">By module</div>
              <button onClick={() => setFilter({ ...filter, module: "" })} className={`w-full text-left text-sm py-1 px-2 rounded ${!filter.module ? "bg-zinc-100 font-medium" : ""}`} data-testid="module-all">
                All ({stats.total || 0})
              </button>
              {Object.entries(stats.by_module || {}).map(([mod, cnt]) => (
                <button key={mod} onClick={() => setFilter({ ...filter, module: mod })} className={`w-full text-left text-sm py-1 px-2 rounded flex justify-between ${filter.module === mod ? "bg-zinc-100 font-medium" : ""}`} data-testid={`module-${mod}`}>
                  <span className="capitalize">{mod.replace(/_/g, " ")}</span>
                  <span className="text-xs text-zinc-500">{cnt}</span>
                </button>
              ))}
            </Card>
          </aside>

          <main className="lg:col-span-3">
            <Card className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Type</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Module</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reporter</TableHead>
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-6">Loading…</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 italic py-6">Nothing in this view yet — encourage your team to use the Report button on each page.</TableCell></TableRow>
                  ) : filtered.map((it) => {
                    const tm = TYPE_META[it.type] || TYPE_META.question;
                    const sev = SEV_BADGE[it.severity] || SEV_BADGE.normal;
                    const Icon = tm.Icon;
                    return (
                      <TableRow key={it.id} className="cursor-pointer hover:bg-zinc-50" onClick={() => setOpenItem(it)} data-testid={`row-${it.id}`}>
                        <TableCell><Icon size={20} weight="duotone" className={tm.color} /></TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{it.title}</div>
                          {it.page_url && <div className="text-[10px] font-mono text-zinc-400">{it.page_url}</div>}
                        </TableCell>
                        <TableCell className="text-xs">
                          {it.tenant_name ? (
                            <>
                              <div className="font-medium text-zinc-700">{it.tenant_name}</div>
                              {it.tenant_plan && <div className="text-[10px] text-zinc-400 uppercase">{it.tenant_plan}</div>}
                            </>
                          ) : <span className="text-zinc-400 italic">—</span>}
                        </TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{(it.module || "other").replace(/_/g, " ")}</Badge></TableCell>
                        <TableCell><Badge className={sev.className} variant={sev.variant}>{sev.label}</Badge></TableCell>
                        <TableCell><Badge variant={it.status === "new" ? "default" : it.status === "resolved" || it.status === "verified" ? "secondary" : "outline"}>{it.status}</Badge></TableCell>
                        <TableCell className="text-xs">{it.reporter_name}<br/><span className="text-zinc-400">{it.reporter_role}</span></TableCell>
                        <TableCell className="text-xs text-zinc-500">{(it.updated_at || "").slice(0, 10)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </main>
        </div>
      </div>

      <ItemDrawer item={openItem} onClose={() => setOpenItem(null)} onSaved={() => { setOpenItem(null); load(); }} />
    </AppShell>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <Card className={`p-4 ${highlight ? "border-zinc-900 border-2" : ""}`}>
      <div className="text-xs text-zinc-500 uppercase">{label}</div>
      <div className={`text-3xl font-semibold mt-1 ${highlight && value > 0 ? "text-zinc-900" : "text-zinc-700"}`}>{value}</div>
    </Card>
  );
}

function FilterPills({ label, current, options, onPick, testid }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-zinc-500 mr-1">{label}:</span>
      <button onClick={() => onPick("")} className={`px-2.5 py-1 text-xs rounded-full ${!current ? "bg-zinc-900 text-white" : "bg-zinc-100"}`} data-testid={`${testid}-all`}>All</button>
      {options.map((o) => (
        <button key={o} onClick={() => onPick(o)} className={`px-2.5 py-1 text-xs rounded-full capitalize ${current === o ? "bg-zinc-900 text-white" : "bg-zinc-100"}`} data-testid={`${testid}-${o}`}>{o.replace(/_/g, " ")}</button>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
function ItemDrawer({ item, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item) return;
    (async () => {
      try {
        const r = await api.get(`/tracker/items/${item.id}`);
        setDetail(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [item]);

  const patch = async (updates) => {
    setSaving(true);
    try {
      await api.patch(`/tracker/items/${item.id}`, updates);
      const r = await api.get(`/tracker/items/${item.id}`);
      setDetail(r.data);
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tracker/items/${item.id}/comment`, { body: comment });
      setComment("");
      const r = await api.get(`/tracker/items/${item.id}`);
      setDetail(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!item) return null;
  const it = detail?.item || item;

  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="!w-full sm:!max-w-2xl overflow-y-auto" data-testid="item-drawer">
        <SheetHeader>
          <SheetTitle className="pr-8">{it.title}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="text-xs text-zinc-500 flex flex-wrap items-center gap-2">
            <span>Reported by <strong>{it.reporter_name}</strong></span>
            <span>·</span>
            <span>{(it.created_at || "").slice(0, 16).replace("T", " ")}</span>
            {it.page_url && (<><span>·</span><span className="font-mono">{it.page_url}</span></>)}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase">Status</Label>
              <Select value={it.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger data-testid="drawer-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase">Severity</Label>
              <Select value={it.severity} onValueChange={(v) => patch({ severity: v })}>
                <SelectTrigger data-testid="drawer-severity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blocker">Blocker</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase">Type</Label>
              <Select value={it.type} onValueChange={(v) => patch({ /* type cant change post-create */ })} disabled>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={it.type}>{it.type}</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between border-y border-zinc-200 py-3">
            <div className="flex items-center gap-2">
              <Megaphone size={16} weight="duotone" className="text-indigo-600" />
              <span className="text-sm">Publish to public roadmap</span>
            </div>
            <Switch checked={!!it.public_roadmap} onCheckedChange={(v) => patch({ public_roadmap: v })} data-testid="public-toggle" />
          </div>

          {it.description && (
            <Card className="p-3 bg-zinc-50 whitespace-pre-wrap text-sm">{it.description}</Card>
          )}

          {/* Comments thread */}
          <div>
            <Label className="text-xs uppercase">Activity</Label>
            <div className="space-y-2 mt-2">
              {(detail?.comments || []).map((c) => (
                <div key={c.id} className={`p-2 rounded text-sm ${c.system ? "bg-amber-50 italic text-xs" : "bg-zinc-100"}`}>
                  <div className="text-[10px] text-zinc-500"><strong>{c.author_name}</strong> · {(c.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  <div className="text-zinc-800 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: c.body.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }} />
                </div>
              ))}
              {(detail?.comments || []).length === 0 && (
                <div className="text-xs text-zinc-400 italic">No comments yet.</div>
              )}
            </div>
            <div className="mt-2 flex gap-2">
              <Textarea rows={2} placeholder="Add a comment…" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="comment-input" />
              <Button onClick={sendComment} data-testid="send-comment-btn">Post</Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Label({ children, className = "" }) {
  return <div className={`text-xs font-medium text-zinc-500 mb-1 ${className}`}>{children}</div>;
}
